import * as echarts from "echarts";

interface RegisterSvgImageMapOptions {
  mapName: string;
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
}

const registeredMapSources = new Map<string, string>();

/**
 * Registers a raster image (PNG/JPEG) as an ECharts geo map, replacing a prior
 * source registered under the same `mapName`. ECharts' `registerMap` only
 * accepts SVG or GeoJSON, so the image is wrapped in a minimal SVG whose
 * viewBox matches the image's native pixel dimensions and which places the
 * image with a single `<image>` element at
 * (0, 0). This keeps scatter series coordinates in the same pixel space as the
 * source image, while preserving the geo layer's built-in roam (zoom/pan)
 * support.
 */
export function registerSvgImageMap({
  mapName,
  imageUrl,
  imageWidth,
  imageHeight,
}: RegisterSvgImageMapOptions): void {
  const sourceKey = JSON.stringify([imageUrl, imageWidth, imageHeight]);
  if (registeredMapSources.get(mapName) === sourceKey) return;

  // Escape special XML characters in the URL for safe embedding in SVG attributes
  const escapedUrl = imageUrl
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${imageWidth} ${imageHeight}"><image href="${escapedUrl}" x="0" y="0" width="${imageWidth}" height="${imageHeight}" /></svg>`;

  echarts.registerMap(mapName, { svg });
  registeredMapSources.set(mapName, sourceKey);
}
